import React, { useState, useRef, useEffect } from 'react';
import { Box, Flex, Text, Tooltip } from '@chakra-ui/react';
import { FiDroplet } from 'react-icons/fi';
import { THEME_LIST } from '../themes';

// Solid color swatch representing each theme's focus accent
const SWATCH_COLORS = {
  midnight: ['#1e1040', '#a78bfa', '#0d0b1e'],
  aurora:   ['#0c3050', '#2dd4bf', '#0a1828'],
  warmGlow: ['#2d1200', '#fb923c', '#160900'],
  deepFocus:['#0f0f14', '#e2e8f0', '#070710'],
  rainGlass:['#051228', '#38bdf8', '#07192c'],
};

const ThemeSwitcher = ({ currentTheme, onThemeChange, textColor }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <Box position="relative" ref={ref}>
      <Tooltip label="Theme" placement="bottom">
        <Box
          as="button"
          type="button"
          onClick={() => setOpen((o) => !o)}
          display="flex"
          alignItems="center"
          justifyContent="center"
          p={2}
          borderRadius="lg"
          cursor="pointer"
          aria-label={`Theme — ${THEME_LIST.find((t) => t.id === currentTheme)?.name ?? 'choose theme'}`}
          style={{
            background: 'rgba(255,255,255,0.05)',
            border: '1px solid rgba(255,255,255,0.1)',
            color: textColor,
            backdropFilter: 'blur(8px)',
            transition: 'all 0.15s ease',
          }}
          _hover={{ background: 'rgba(255,255,255,0.09)' }}
        >
          <FiDroplet size={15} strokeWidth={2} aria-hidden />
        </Box>
      </Tooltip>

      {open && (
        <Box
          position="absolute"
          top="calc(100% + 8px)"
          right={0}
          zIndex={200}
          borderRadius="xl"
          p={2}
          minW="180px"
          style={{
            background: 'rgba(12,10,28,0.95)',
            border: '1px solid rgba(255,255,255,0.1)',
            backdropFilter: 'blur(20px)',
            boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
          }}
        >
          {THEME_LIST.map((t) => {
            const [dark, accent] = SWATCH_COLORS[t.id];
            const isActive = t.id === currentTheme;
            return (
              <Flex
                key={t.id}
                as="button"
                type="button"
                alignItems="center"
                gap={3}
                w="full"
                px={3}
                py={2.5}
                borderRadius="lg"
                cursor="pointer"
                onClick={() => { onThemeChange(t.id); setOpen(false); }}
                style={{
                  background: isActive ? 'rgba(255,255,255,0.08)' : 'transparent',
                  border: isActive ? '1px solid rgba(255,255,255,0.1)' : '1px solid transparent',
                  transition: 'all 0.15s ease',
                  width: '100%',
                  textAlign: 'left',
                }}
                _hover={{ background: 'rgba(255,255,255,0.06)' }}
              >
                {/* Mini swatch */}
                <Box
                  w="24px"
                  h="24px"
                  borderRadius="md"
                  flexShrink={0}
                  alignSelf="center"
                  style={{
                    background: `linear-gradient(135deg, ${dark} 0%, ${accent} 100%)`,
                    boxShadow: isActive ? `0 0 8px ${accent}88` : 'none',
                  }}
                />
                <Flex flex={1} minW={0} alignItems="center">
                  <Text
                    as="span"
                    fontSize="12px"
                    fontWeight={isActive ? '600' : '400'}
                    lineHeight="1.25"
                    m={0}
                    textAlign="left"
                    color="white"
                  >
                    {t.emoji} {t.name}
                  </Text>
                </Flex>
                {isActive && (
                  <Box flexShrink={0} w="6px" h="6px" borderRadius="full" bg={accent} alignSelf="center" />
                )}
              </Flex>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

export default ThemeSwitcher;
